import { useEffect, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import type { useCapacityStep } from './CapacityStep/useCapacityStep';
import type { usePackageSetupStep } from './PackageSetupStep/usePackageSetupStep';
import type { useOpportunityStep } from './PrerequisitesStep/useOpportunityStep';
import type { usePropertyStep } from './PropertyStep/usePropertyStep';
import type { useRatePlanStep } from './RatePlanStep/useRatePlanStep';
import type { useRoomRatesStep } from './RoomRatesStep/useRoomRatesStep';
import type { useRoomsStep } from './RoomsStep/useRoomsStep';
import type { useSubmissionStep } from './SubmissionStep/useSubmissionStep';
import type { useTaxesFeesStep } from './TaxesFeesStep/useTaxesFeesStep';
import { STEPS } from './constants';

export interface UseWizardDialogParams {
  activeStep: number;
  capacityStep: ReturnType<typeof useCapacityStep>;
  isStepBusy: boolean;
  opportunity: ReturnType<typeof useOpportunityStep>;
  packageSetupStep: ReturnType<typeof usePackageSetupStep>;
  propertyStep: ReturnType<typeof usePropertyStep>;
  ratePlanStep: ReturnType<typeof useRatePlanStep>;
  roomRatesStep: ReturnType<typeof useRoomRatesStep>;
  roomsStep: ReturnType<typeof useRoomsStep>;
  selectedPropertyHasRooms: boolean;
  setActiveStep: (value: number | ((prev: number) => number)) => void;
  setErrorMessage: (message: string | null) => void;
  setIsAdvancingStep: (isAdvancing: boolean) => void;
  setPropertyWarningMessage: (message: string | null) => void;
  submissionStep: ReturnType<typeof useSubmissionStep>;
  taxesFeesStep: ReturnType<typeof useTaxesFeesStep>;
}

/**
 * Owns the wizard dialog's own lifecycle, separate from any individual step: opening (including
 * deep-linking via `?setupFlashOffer=<salesforceId>`), closing (with the exit-confirmation gate),
 * going back a step, and redirecting to the CM Checklist page once the final step is reached.
 */
export function useWizardDialog({
  activeStep,
  capacityStep,
  isStepBusy,
  opportunity,
  packageSetupStep,
  propertyStep,
  ratePlanStep,
  roomRatesStep,
  roomsStep,
  selectedPropertyHasRooms,
  setActiveStep,
  setErrorMessage,
  setIsAdvancingStep,
  setPropertyWarningMessage,
  submissionStep,
  taxesFeesStep,
}: UseWizardDialogParams) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTriggeredSalesforceOfferId = searchParams.get('setupFlashOffer') ?? '';
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isExitConfirmationOpen, setIsExitConfirmationOpen] = useState(false);

  const { trimmedSalesforceOfferId } = opportunity;
  const isContentStep = activeStep === STEPS.length - 1;
  const contentPageUrl = trimmedSalesforceOfferId
    ? `/offers/${trimmedSalesforceOfferId}/offer-builder/cm-checklist`
    : '';

  function handleOpen() {
    setIsDialogOpen(true);
    setIsExitConfirmationOpen(false);
    opportunity.reset();
    propertyStep.reset();
    roomsStep.reset();
    ratePlanStep.reset();
    roomRatesStep.reset();
    capacityStep.reset();
    packageSetupStep.reset();
    taxesFeesStep.reset();
    submissionStep.reset();
    setIsAdvancingStep(false);
    setErrorMessage(null);
    setPropertyWarningMessage(null);
    setActiveStep(0);
    ratePlanStep.initializeRatePlanDrafts();
  }

  // Supports deep-linking into this wizard via `?setupFlashOffer=<salesforceId>`.
  useEffect(() => {
    if (urlTriggeredSalesforceOfferId && !isDialogOpen) {
      handleOpen();
      opportunity.setSalesforceOfferId(urlTriggeredSalesforceOfferId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('setupFlashOffer');
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTriggeredSalesforceOfferId]);

  useEffect(() => {
    if (isDialogOpen && isContentStep && contentPageUrl && opportunity.isResyncComplete) {
      navigate(contentPageUrl);
    }
  }, [contentPageUrl, isContentStep, isDialogOpen, opportunity.isResyncComplete, navigate]);

  function goToContentPage() {
    if (contentPageUrl && opportunity.isResyncComplete) {
      navigate(contentPageUrl);
    }
  }

  async function retryResync() {
    const { errorMessage } = await opportunity.retryResync();
    setErrorMessage(errorMessage ?? null);
  }

  function handleClose() {
    if (isStepBusy) {
      return;
    }

    if (activeStep === 0 && !trimmedSalesforceOfferId) {
      handleConfirmClose();
      return;
    }

    setIsExitConfirmationOpen(true);
  }

  function handleCancelClose() {
    setIsExitConfirmationOpen(false);
  }

  function handleConfirmClose() {
    opportunity.resetBusyState();
    propertyStep.setIsCreatingNewProperty(false);
    ratePlanStep.setIsCreatingRatePlan(false);
    roomsStep.setIsCreatingRooms(false);
    roomRatesStep.setIsCreatingRoomRates(false);
    setIsAdvancingStep(false);
    setIsExitConfirmationOpen(false);
    setIsDialogOpen(false);
  }

  function handlePrevious() {
    setErrorMessage(null);
    setActiveStep((prev) => (prev === 4 && selectedPropertyHasRooms ? 2 : Math.max(prev - 1, 0)));
  }

  return {
    contentPageUrl,
    goToContentPage,
    handleCancelClose,
    handleClose,
    handleConfirmClose,
    handleOpen,
    handlePrevious,
    isDialogOpen,
    isExitConfirmationOpen,
    retryResync,
  };
}
